import React, { useEffect, useMemo, useState } from "react";
import {
  Container, Card, Table, Spinner, Alert, Button, Modal,
  Badge
} from "react-bootstrap";
import API from "../services/api";
import "./AdminReports.css";

/* utils */
const fmtIST = (d) => d ? new Date(d).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—";
const orDash = (v) => (v === 0 ? "0" : (v == null || String(v).trim() === "" ? "—" : v));
const yesChip = (b) => b ? <Badge bg="success">Yes</Badge> : <Badge bg="secondary">No</Badge>;
const asYes = (v) => {
  if (typeof v === "boolean") return v;
  const s = String(v || "").toLowerCase();
  return s === "yes" || s === "true" || s === "1";
};
const phone = (x) => x ? <a href={`tel:${x}`}>{x}</a> : "—";
const mail = (x) => x ? <a href={`mailto:${x}`}>{x}</a> : "—";

/* map link helper */
const toLocText = (locText, loc) => {
  if (locText) return locText;
  if (!loc) return "—";
  if (typeof loc === "string") return loc.trim() || "—";
  const { address, line1, area, city, state, pincode, lat, lng, latitude, longitude } = loc || {};
  const parts = [address, line1, area, city, state, pincode].map(x => (x||"").toString().trim()).filter(Boolean);
  if (parts.length) return parts.join(", ");
  const la = lat ?? latitude, lo = lng ?? longitude;
  return (la!=null && lo!=null) ? "Location captured" : "—";
};
const MapA = ({lat,lng,text}) => {
  if (lat==null || lng==null) return <>{text || "—"}</>;
  return <a href={`https://www.google.com/maps?q=${lat},${lng}`} target="_blank" rel="noreferrer">{text || "View location"} <span>📍</span></a>;
};

export default function Reports() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [sel, setSel] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await API.get("/reports");
        setData(Array.isArray(data) ? data : []);
        setErr("");
      } catch (e) {
        console.error(e);
        setErr("❌ Failed to load reports");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <Container className="mt-4 admin-reports-shell">
      <Card className="p-4 shadow-sm admin-reports-card">
        <div className="admin-reports-header">
          <div className="admin-reports-title">
            <h2 className="mb-0">Employee Reports</h2>
            <div className="admin-reports-sub">
              <Badge bg="primary" className="admin-reports-count">{data.length} total</Badge>
              <span>Reports submitted by field staff</span>
            </div>
          </div>
          <div className="admin-reports-actions">
            <Button variant="outline-secondary" size="sm">Filter</Button>
            <Button variant="primary" size="sm">Export CSV</Button>
          </div>
        </div>

        {err && <Alert variant="danger" className="mt-3">{err}</Alert>}

        {loading ? (
          <div className="text-center my-4"><Spinner animation="border" /></div>
        ) : (
          <>
            <div className="admin-reports-table-wrap">
              <Table bordered hover responsive className="mt-3 align-middle admin-reports-table">
                <thead className="admin-reports-thead">
                  <tr>
                    <th>#</th>
                    <th>Employee</th>
                    <th>Vendor</th>
                    <th>Product</th>
                    <th>Availability</th>
                    <th>Submitted</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.length ? data.map((r,i) => {
                const vendorName =
                  r._display?.vendorName ||
                  r.snapshot?.vendor?.companyName ||
                  r.requirement?.vendorId?.companyName ||
                  r.requirementId?.vendorId?.companyName ||
                  r.requirement?.vendorId?.userId?.fullName ||
                  r.requirementId?.vendorId?.userId?.fullName ||
                  r.vendor?.fullName ||
                  "N/A";

                const vendorMobile =
                  r._display?.vendorMobile ||
                  r.snapshot?.vendor?.mobile ||
                  r.requirement?.vendorId?.userId?.mobile ||
                  r.requirementId?.vendorId?.userId?.mobile ||
                  r.vendor?.mobile ||
                  "";

                const productText =
                  r._display?.product ||
                  r.snapshot?.requirement?.productType ||
                  r.requirement?.productType ||
                  r.requirementId?.productType ||
                  "—";

                return (
                  <tr key={r._id} className="admin-reports-row">
                    <td>{i+1}</td>
                    <td>
                      <div className="admin-name">{orDash(r.employee?.fullName)}</div>
                      {r.employee?.mobile ? <div className="text-muted small">{r.employee.mobile}</div> : null}
                    </td>
                    <td>
                      <div className="admin-name">{vendorName}</div>
                      {vendorMobile ? <div className="text-muted small">{vendorMobile}</div> : null}
                    </td>
                    <td>{productText}</td>
                    <td className="admin-cell-status">
                      <span className={`admin-availability ${asYes(r.hasRequirement) ? "is-yes" : "is-no"}`}>
                        {asYes(r.hasRequirement) ? "Available" : "Unavailable"}
                      </span>
                    </td>
                    <td className="admin-cell-date">{fmtIST(r.createdAt)}</td>
                    <td className="admin-cell-action">
                      <Button size="sm" variant="link" className="admin-view-btn" onClick={() => setSel(r)}>View Full Report</Button>
                    </td>
                  </tr>
                );
                  }) : (
                    <tr><td colSpan={7} className="text-center">No reports</td></tr>
                  )}
                </tbody>
              </Table>
            </div>

            <div className="admin-reports-cards">
              {data.length ? data.map((r,i) => {
                const vendorName =
                  r._display?.vendorName ||
                  r.snapshot?.vendor?.companyName ||
                  r.requirement?.vendorId?.companyName ||
                  r.requirementId?.vendorId?.companyName ||
                  r.requirement?.vendorId?.userId?.fullName ||
                  r.requirementId?.vendorId?.userId?.fullName ||
                  r.vendor?.fullName ||
                  "N/A";

                const vendorMobile =
                  r._display?.vendorMobile ||
                  r.snapshot?.vendor?.mobile ||
                  r.requirement?.vendorId?.userId?.mobile ||
                  r.requirementId?.vendorId?.userId?.mobile ||
                  r.vendor?.mobile ||
                  "";

                const productText =
                  r._display?.product ||
                  r.snapshot?.requirement?.productType ||
                  r.requirement?.productType ||
                  r.requirementId?.productType ||
                  "—";

                return (
                  <div key={r._id} className="admin-report-card">
                    <div className="admin-report-top">
                      <div>
                        <div className="admin-report-name">{orDash(r.employee?.fullName)}</div>
                        <div className="admin-report-sub">
                          {r.employee?.mobile || "—"}
                        </div>
                      </div>
                      <div className="admin-report-chip">{yesChip(asYes(r.hasRequirement))}</div>
                    </div>
                    <div className="admin-report-row">
                      <span className="label">Vendor</span>
                      <span className="value">{vendorName}</span>
                    </div>
                    <div className="admin-report-row">
                      <span className="label">Vendor Mobile</span>
                      <span className="value">{vendorMobile || "—"}</span>
                    </div>
                    <div className="admin-report-row">
                      <span className="label">Product</span>
                      <span className="value">{productText}</span>
                    </div>
                    <div className="admin-report-row">
                      <span className="label">Submitted</span>
                      <span className="value">{fmtIST(r.createdAt)}</span>
                    </div>
                    <div className="admin-report-actions">
                      <Button size="sm" onClick={() => setSel(r)}>View</Button>
                    </div>
                  </div>
                );
              }) : (
                <div className="text-center mt-3">No reports</div>
              )}
            </div>
          </>
        )}
      </Card>

      <Details report={sel} onHide={() => setSel(null)} />
    </Container>
  );
}

/* ===== Details Modal ===== */
function Details({report, onHide}) {
  const show = !!report;

  const meta = useMemo(() => {
    if (!report) return null;

    const snap = report.snapshot || {};
    const reqLive = report.requirement || report.requirementId || {};
    const reqS = snap.requirement || {};
    const venS = snap.vendor || {};
    const empS = snap.employee || {};
    const extra = report.extra || {};

    // vendor may be Vendor doc (with userId) or direct User
    const venLiveAny = reqLive.vendorId || report.vendor || {};
    const venUserMaybe = venLiveAny.userId || venLiveAny;

    // location
    const locObj = reqLive.location || {};
    const lat = locObj?.lat ?? locObj?.latitude;
    const lng = locObj?.lng ?? locObj?.longitude;
    const locText = toLocText(
      reqS.locationText || report._display?.locationText,
      reqLive.location
    );

    const product =
      reqS.productType ||
      reqLive.productType ||
      report._display?.product ||
      "—";

    const qty = (reqS.quantity ?? reqLive.quantity);
    const unit = reqS.unit || reqLive.unit;

    const vendor = {
      company:
        venS.companyName ||
        venLiveAny.companyName ||
        report._display?.vendorName ||
        venUserMaybe.fullName ||
        "N/A",
      name:
        venS.fullName ||
        venUserMaybe.fullName ||
        "",
      mobile:
        venS.mobile ||
        venUserMaybe.mobile ||
        "",
      email:
        venS.email ||
        venUserMaybe.email ||
        "",
      gst:
        venS.gstNumber ||
        venLiveAny.gstNumber ||
        "",
      address:
        venS.address ||
        venLiveAny.address ||
        "",
    };

    return {
      product,
      qty, unit,
      notes: reqS.notes || reqLive.notes || "",
      locText, lat, lng,

      vendor,

      employee: {
        name: report.employee?.fullName || empS.fullName || "—",
        mobile: report.employee?.mobile || empS.mobile || "",
        email: report.employee?.email || empS.email || "",
      },

      available: asYes(report.hasRequirement),
      budget: report.budget || "",
      process: report.process || "",
      items: report.items || "",
      message: report.message || "",
      when: report.createdAt,

      extra,
      attachments: Array.isArray(report.attachments) ? report.attachments : [],
    };
  }, [report]);

  if (!show) return null;

  const chip = meta.available ? <Badge bg="success">Available</Badge> : <Badge bg="secondary">Not Available</Badge>;

  return (
    <Modal show onHide={onHide} size="xl" centered dialogClassName="admin-report-modal">
      <Modal.Header closeButton className="admin-modal-header-advanced">
        <div className="admin-modal-header-content">
          <span className="admin-modal-pill">Report</span>
          <div className="admin-modal-header-text">
            <h2 className="admin-modal-title-advanced">{meta.product}</h2>
            <p className="admin-modal-subtitle">
              Submitted {fmtIST(meta.when)} • {orDash(meta.employee.name)}
            </p>
          </div>
        </div>
        <div className="admin-modal-chip">{chip}</div>
      </Modal.Header>
      <Modal.Body className="admin-modal-body admin-modal-body-advanced">
        <div className="admin-modal-advanced">
          <div className="admin-modal-left">
            <section className="admin-adv-section">
              <h3 className="admin-adv-title">Requirement Details</h3>
              <div className="admin-adv-card">
                <div className="admin-adv-row">
                  <div className="admin-adv-label">Required Quantity</div>
                  <div className="admin-adv-value">{meta.qty!=null ? <>{meta.qty} {meta.unit||""}</> : "—"}</div>
                </div>
                <div className="admin-adv-row">
                  <div className="admin-adv-label">Site Location</div>
                  <div className="admin-adv-value">
                    <MapA lat={meta.lat} lng={meta.lng} text={meta.locText} />
                  </div>
                </div>
                <div className="admin-adv-note">{orDash(meta.notes)}</div>
              </div>
            </section>

            <section className="admin-adv-section">
              <h3 className="admin-adv-title">Report Insights</h3>
              <div className="admin-adv-highlight">
                <div className="admin-adv-highlight-title">Summary</div>
                <div className="admin-adv-highlight-grid">
                  <div>
                    <div className="admin-adv-label">Budget</div>
                    <div className="admin-adv-value">{orDash(meta.budget)}</div>
                  </div>
                  <div>
                    <div className="admin-adv-label">Process</div>
                    <div className="admin-adv-value">{orDash(meta.process)}</div>
                  </div>
                  <div>
                    <div className="admin-adv-label">Items</div>
                    <div className="admin-adv-value">{orDash(meta.items)}</div>
                  </div>
                  <div>
                    <div className="admin-adv-label">Message</div>
                    <div className="admin-adv-value">{orDash(meta.message)}</div>
                  </div>
                </div>
              </div>
            </section>

            <section className="admin-adv-section">
              <h3 className="admin-adv-title">Availability & Commercials</h3>
              <div className="admin-adv-metrics">
                <div className="admin-adv-metric">
                  <div className="admin-adv-label">Available Qty</div>
                  <div className="admin-adv-metric-value">{orDash(meta.extra?.availableQty)}</div>
                </div>
                <div className="admin-adv-metric">
                  <div className="admin-adv-label">Price / Unit</div>
                  <div className="admin-adv-metric-value">{meta.extra?.pricePerUnit ? `${meta.extra.pricePerUnit} ${meta.extra.currency||""}` : "—"}</div>
                </div>
                <div className="admin-adv-metric">
                  <div className="admin-adv-label">MOQ</div>
                  <div className="admin-adv-metric-value">{orDash(meta.extra?.moq)}</div>
                </div>
                <div className="admin-adv-metric">
                  <div className="admin-adv-label">Lead Time (days)</div>
                  <div className="admin-adv-metric-value">{orDash(meta.extra?.leadTimeDays)}</div>
                </div>
              </div>
            </section>

            {meta.attachments.length ? (
              <section className="admin-adv-section">
                <h3 className="admin-adv-title">Attachments</h3>
                <div className="admin-adv-attachments">
                  {meta.attachments.map((p, i) => (
                    <a key={i} className="admin-adv-attachment" href={p} target="_blank" rel="noreferrer">
                      <span>File {i+1}</span>
                      <span className="admin-attachment-action">Download</span>
                    </a>
                  ))}
                </div>
              </section>
            ) : null}
          </div>

          <aside className="admin-modal-right">
            <div className="admin-side-card-advanced">
              <h4 className="admin-side-title-advanced">Vendor Info</h4>
              <div className="admin-side-name">{orDash(meta.vendor.company)}</div>
              <div className="admin-side-sub">{orDash(meta.vendor.name)}</div>
              <div className="admin-side-row">GST: {orDash(meta.vendor.gst)}</div>
              <div className="admin-side-row">Mobile: {phone(meta.vendor.mobile)}</div>
              <div className="admin-side-row">Email: {mail(meta.vendor.email)}</div>
              <div className="admin-side-row">Address: {orDash(meta.vendor.address)}</div>
            </div>
            <div className="admin-side-card-advanced">
              <h4 className="admin-side-title-advanced">Employee Details</h4>
              <div className="admin-side-name">{orDash(meta.employee.name)}</div>
              <div className="admin-side-row">Mobile: {phone(meta.employee.mobile)}</div>
              <div className="admin-side-row">Email: {mail(meta.employee.email)}</div>
            </div>
            <div className="admin-side-card-advanced admin-side-map">
              <div className="admin-side-map-title">Field Location</div>
              <MapA lat={meta.lat} lng={meta.lng} text={meta.locText} />
            </div>
          </aside>
        </div>
      </Modal.Body>
      <Modal.Footer className="admin-modal-footer admin-modal-footer-advanced">
        <div className="admin-modal-footer-left">
          <Button variant="outline-secondary">Flag for Revision</Button>
          <Button variant="outline-danger">Reject</Button>
        </div>
        <div className="admin-modal-footer-right">
          <Button variant="outline-secondary" onClick={onHide}>Close</Button>
          <Button variant="primary">Approve Procurement</Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

